import { useEffect, useState } from 'react';

import { PROMOTION_DISPLAY_THRESHOLD_DAYS } from '@domains/pdp-common';

interface UsePromotionTimerServiceProps {
  // 종료일이 현재 시간 기준 14일 이후인 경우 서버에서 null 반환
  timestamp: number | null;
  onExpire?: () => void;
}

const SECOND = 1000;
const MINUTE = SECOND * 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;

export const usePromotionTimerService = ({ timestamp, onExpire }: UsePromotionTimerServiceProps) => {
  // 1초마다 리렌더링을 트리거하여 getFormattedCountdown()이 새로운 시간값을 반환하도록 함
  const [, setTick] = useState(0);
  const [isClient, setIsClient] = useState(false);
  const OVER_THRESHOLD_TIME_TEXT = '특가 종료 시';

  const getFormattedCountdown = (): string => {
    if (!isClient) {
      return '';
    }

    if (timestamp === null) {
      return OVER_THRESHOLD_TIME_TEXT;
    }

    const now = Date.now();
    const end = new Date(timestamp).getTime();
    const diff = end - now;

    // 종료된 경우
    if (diff <= 0) {
      onExpire?.();
      return '00:00:00';
    }

    const days = Math.floor(diff / DAY);

    // PROMOTION_DISPLAY_THRESHOLD_DAYS일 초과
    if (days > PROMOTION_DISPLAY_THRESHOLD_DAYS) {
      return OVER_THRESHOLD_TIME_TEXT;
    }

    // 1일 < 잔여기간 <= PROMOTION_DISPLAY_THRESHOLD_DAYS일
    if (days >= 1) {
      return `${days}일`;
    }

    // 1일 이하
    const hours = Math.floor(diff / HOUR);
    const minutes = Math.floor((diff % HOUR) / MINUTE);
    const seconds = Math.floor((diff % MINUTE) / SECOND);

    const hh = hours.toString().padStart(2, '0');
    const mm = minutes.toString().padStart(2, '0');
    const ss = seconds.toString().padStart(2, '0');

    return `${hh}:${mm}:${ss}`;
  };

  // PROMOTION_DISPLAY_THRESHOLD_DAYS일 초과("특가 종료 시")일 때 타이머 아이콘 미노출
  const shouldShowTimerIcon = (): boolean => {
    if (!isClient || timestamp === null) {
      return false;
    }
    const now = Date.now();
    const end = new Date(timestamp).getTime();
    const diff = end - now;
    const days = Math.floor(diff / DAY);
    return days <= PROMOTION_DISPLAY_THRESHOLD_DAYS;
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!timestamp) {
      return;
    }

    const interval = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timestamp]);

  return {
    getFormattedCountdown,
    shouldShowTimerIcon,
    isClient,
  };
};
